/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { TimeStepOptions } from "@mui/x-date-pickers";

interface ConstType {
  /** check notification interval.[ms] */
  eventInterval: number;
  /** check map update interval.[ms] */
  mapCheckInterval: number;
  /** awv info get interval.[ms] */
  awvInterval: number;
  /** task get interval.[ms] */
  taskInterval: number;
  /** display hours in tasklist. */
  timelineRange: number;
  /** Front-left camera */
  cameraFrontLeft: number;
  /** Front-right camera */
  cameraFrontRight: number;
  /** Left camera */
  cameraLeft: number;
  /** Right camera */
  cameraRight: number;
  /** Rear camera */
  cameraRear: number;
  /** webrtcStatus: opening */
  webRtcStatusOpening: string;
  /** webrtcStatus: streaming */
  webRtcStatusStreaming: string;
  /** webrtcStatus: disconnected */
  webRtcStatusDisconnected: string;
  /** webrtcEnable: Start streaming (2) */
  webRtcEnableStart: number;
  /** webrtcEnable: Stop streaming (1) */
  webRtcEnableStop: number;
  /** webrtcEnable: Streaming status not changed (0) */
  webRtcEnableGoing: number;
  /** peer connection interval.[ms] */
  peerConnectionInterval: number;
  /** Background color of side panel header */
  sidePanelHeaderColor: string;
  /** Background color of side panel body */
  sidePanelBodyColor: string;
  /** Background color of side panel footer */
  sidePanelFooterColor: string;
  operationTimeout: {
    updateToken: number;
    deleteTask: number;
    getTask: number;
    cancelTask: number;
    goHomeTask: number;
    skipTask: number;
    registerTask: number;
    updateTask: number;
    approveTask: number;
    getNotificationList: number;
    saveRoadClosure: number;
    mapCheckUpdate: number;
    getMapInfo: number;
    getAwvInfoList: number;
    getProjectList: number;
    cameraTask: number;
    usersTask: number;
    getTaskList: number;
    getTaskCreationProgress: number;
    changeApproved: number;
    webRtcConnect: number;
    getChargers: number;
    getMaterials: number;
    getUsers: number;
    getJob: number;
  };
  spinnerDelayInterval: number;
  zIndex: {
    edge: number;
    node: number;
    namedNode: number;
    landmarkNode: number;
    roadStopNode: number;
    awv: number;
    offlineAwvs: number;
    userPosition: number;
    magnify: number;
    highlight: number;
    autoStart: number;
  };
  notificationPageSize: number;
  /** Date and time string format */
  dateTimeFormat: string;
  /** Date string format */
  dateFormat: string;
  /** Date string format for GET tasks API */
  getTaskDateFormat: string;
  /** Node/Edge attribute */
  priorityClosed: number;
  /** Node/Edge attribute */
  priorityNotClosed: number;
  /** Error code */
  errorCodes: {
    mapUnlockFailed: string;
    mapUpdateConflict: string;
    mapVerIsNotLatest: string;
    taskUpdateConflict: string;
    taskStatusIsCompleated: string;
  };
  /** Get route data of task or not */
  removeNode: {
    necessary: number;
    unnecessary: number;
  };
  jobsetStatus: {
    created: number;
    creationFailed: number;
    notApproved: number;
  };
  taskStatus: {
    running: number;
    notApproved: number;
  };
  taskProgressInterval: number;
  taskCard: {
    cardWhiteColor: string;
    cardGreenColor: string;
    cardGreyColor: string;
    frameGreyColor: string;
    frameGreenColor: string;
    frameOrangeColor: string;
    registeredMaskColor: string;
  };
  /** Path */
  path: {
    root: string;
    map: string;
    tasklist: string;
    camera: string;
    userguide: string;
    about: string;
    unlock: string;
    general: string;
    multiple: string;
  };
  taskStartTimeInputStep: TimeStepOptions;
  cameraClockInterval: number;
  webRtcConnectInterval: number;
  awvIconMinSize: number;
  awvSpecSize: number;
  awvIconSizeUpdateWait: number;
  taskKind: {
    aToB: number;
    multiple: number;
    multipleCharge: number;
    multipleVehicle: number;
    mower: number;
    manual: number;
    goHome: number;
  };
  regExp: {
    asciiPattern: string;
    integerPattern: string;
  };
}

export const Const: ConstType = {
  eventInterval: 5000,
  mapCheckInterval: 60000,
  awvInterval: 2000,
  taskInterval: 10000,
  timelineRange: 4,
  cameraFrontLeft: 2,
  cameraFrontRight: 3,
  cameraLeft: 1,
  cameraRight: 4,
  cameraRear: 5,
  webRtcStatusOpening: "opening",
  webRtcStatusStreaming: "streaming",
  webRtcStatusDisconnected: "disconnected",
  webRtcEnableStart: 2,
  webRtcEnableStop: 1,
  webRtcEnableGoing: 0,
  peerConnectionInterval: 1000,
  sidePanelHeaderColor: "#F0F3F5",
  sidePanelBodyColor: "#E6E9EB",
  sidePanelFooterColor: "#F0F3F5",
  operationTimeout: {
    updateToken: 90000,
    getProjectList: 40000,
    getMapInfo: 40000,
    mapCheckUpdate: 40000,
    getTask: 50000,
    cancelTask: 50000,
    skipTask: 50000,
    goHomeTask: 45000,
    updateTask: 45000,
    deleteTask: 33000,
    getTaskList: 50000,
    registerTask: 45000,
    getTaskCreationProgress: 45000,
    saveRoadClosure: 720000,
    getNotificationList: 35000,
    approveTask: 38000,
    getAwvInfoList: 40000,
    cameraTask: 35000,
    usersTask: 33000,
    changeApproved: 90000,
    webRtcConnect: 30000,
    getChargers: 40000,
    getMaterials: 40000,
    getUsers: 40000,
    getJob: 50000,
  },
  spinnerDelayInterval: 2000,
  zIndex: {
    edge: 100,
    node: 200,
    namedNode: 300,
    landmarkNode: 350,
    userPosition: 400,
    roadStopNode: 500,
    highlight: 600,
    autoStart: 650,
    awv: 700,
    offlineAwvs: 800,
    magnify: 900,
  },
  notificationPageSize: 10,
  dateTimeFormat: "DD/MM/Y HH:mm",
  dateFormat: "DD/MM/Y",
  getTaskDateFormat: "Y-MM-DD",
  priorityClosed: 0,
  priorityNotClosed: 1,
  errorCodes: {
    mapUnlockFailed: "UI-100100037",
    mapUpdateConflict: "300010702",
    mapVerIsNotLatest: "300010802",
    taskUpdateConflict: "299012002",
    taskStatusIsCompleated: "202008012",
  },
  removeNode: {
    necessary: 0,
    unnecessary: 1,
  },
  jobsetStatus: {
    created: 3,
    creationFailed: 5,
    notApproved: 6,
  },
  taskStatus: {
    running: 2,
    notApproved: 6,
  },
  taskProgressInterval: 2000,
  taskCard: {
    cardWhiteColor: "#FFF",
    cardGreenColor: "#CCFFCC",
    cardGreyColor: "#BFBFBF",
    frameGreyColor: "#B3B3B3",
    frameGreenColor: "#A1E5A1",
    frameOrangeColor: "#ffc000",
    registeredMaskColor: "#28E717",
  },
  path: {
    root: "/",
    map: "/map",
    tasklist: "/tasklist",
    camera: "/camera",
    userguide: "/userguide",
    about: "/about",
    unlock: "/unlock",
    general: "*",
    multiple: "/multiple",
  },
  taskStartTimeInputStep: { minutes: 10 },
  cameraClockInterval: 1000,
  webRtcConnectInterval: 10000,
  awvIconMinSize: 30,
  awvSpecSize: 2.962,
  awvIconSizeUpdateWait: 300,
  taskKind: {
    aToB: 0,
    multiple: 1,
    multipleCharge: 3,
    multipleVehicle: 4,
    mower: 5,
    manual: 6,
    goHome: 7,
  },
  regExp: {
    asciiPattern: "[a-zA-Z0-9!-/:-@¥[-`{-~\\s]",
    integerPattern: "[+-]?\\d+",
  },
};

export interface AboutListItem {
  title: string;
  url: string;
}

export const INTERVAL_COUNT = 2000;
